import {
  FormControl,
  FormLabel,
  TextField,
  TextFieldProps,
} from "@mui/material";
import {
  FieldPath,
  FieldValues,
  get,
  UseControllerProps,
  useController,
} from "react-hook-form";
import { ChangeEvent } from "react";

type FormTextFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = Omit<TextFieldProps, "variant" | "type"> &
  UseControllerProps<TFieldValues, TName> & {
    label?: string;
    type?: "text" | "number" | "phone";
  };

export const FormTextField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  id,
  name,
  control,
  rules,
  shouldUnregister,
  defaultValue,
  label,
  helperText,
  fullWidth = true,
  type,
  required,
  ...props
}: FormTextFieldProps<TFieldValues, TName>) => {
  const {
    field: { value, onChange, ref, ...field },
    formState,
  } = useController({
    name,
    control,
    rules,
    defaultValue,
    shouldUnregister,
  });

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const NUMERIC_ONLY = /^[0-9]*$/;
    const PHONE_ONLY = /^\+?[1-9]{0,1}[0-9\s-]*$/;

    if (type === "number" && !NUMERIC_ONLY.test(event.target.value)) {
      return;
    }

    if (type === "phone" && !PHONE_ONLY.test(event.target.value)) {
      return;
    }

    onChange(event);
  };

  const touched = get(formState.touchedFields, name) || formState.isSubmitted;
  const errorMsg = touched ? get(formState.errors, name)?.message : null;

  return (
    <FormControl fullWidth={fullWidth}>
      <FormLabel htmlFor={name} required={required}>
        {label}
      </FormLabel>
      <TextField
        {...field}
        {...props}
        type="text"
        id={id ?? name + "_input"}
        value={value ?? ""}
        helperText={errorMsg ?? helperText}
        onChange={handleChange}
        inputRef={ref}
        error={!!errorMsg}
        fullWidth={fullWidth}
      />
    </FormControl>
  );
};
